import { FeedbackSubmissionModal } from "@/components/feature/Modals/FeedbackSubmissionModal"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Typography } from "@/components/ui/typography"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowLeft, MessageSquare } from "lucide-react"
import { useState } from "react"
import { NavLink, useNavigate } from "react-router"
import { SETTINGS_PATHS, type SettingsSectionId } from "../paths"
import type { SettingsGroupId, SettingsSectionDef } from "../sections"
import { useConfirmLeave } from "./UnsavedChanges"

export interface SettingsNavGroup {
  id: SettingsGroupId
  /** None for the setup checklist, which sits above the groups. */
  labelKey?: TranslationKeys
  sections: SettingsSectionDef[]
}

/** How many setup items are left on each page. */
export type SettingsTodo = Partial<Record<SettingsSectionId, number>>

/** Beside a page in the nav: a dot while it has something left to set up;
 *  on the checklist itself, how many things are left in all. */
function TodoMarker({ count, total }: { count: number; total: boolean }) {
  const { translate } = useTranslation()
  if (count === 0) return null
  const label = translate("SETTINGS/SETUP/NAV_TODO", { count })
  return total ? (
    <span className="ml-auto rounded-full bg-warning/15 px-1.5 text-xs font-medium tabular-nums">
      <span aria-hidden>{count}</span>
      <span className="sr-only">{label}</span>
    </span>
  ) : (
    <span className="ml-auto flex items-center">
      <span aria-hidden className="size-2 rounded-full bg-warning" />
      <span className="sr-only">{label}</span>
    </span>
  )
}

/**
 * Back to where the user opened settings from. Moving between settings pages
 * replaces the history entry, so that page is one step back; with no step
 * back (settings opened in a new tab), home.
 */
function SettingsBackButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const confirmLeave = useConfirmLeave()
  const label = translate("SETTINGS/BACK")

  function goBack() {
    // React Router keeps this tab's history position in `idx`.
    const position = (window.history.state as { idx?: number } | null)?.idx ?? 0
    confirmLeave(() => {
      if (position > 0) void navigate(-1)
      else void navigate("/")
    })
  }

  return iconOnly ? (
    <Button variant="ghost" size="icon" aria-label={label} onClick={goBack}>
      <ArrowLeft />
    </Button>
  ) : (
    <Button
      variant="ghost"
      className="justify-start font-normal"
      onClick={goBack}
    >
      <ArrowLeft />
      {label}
    </Button>
  )
}

/** The settings page list, grouped by who a change affects. */
export function SettingsNav({
  groups,
  todo = {},
}: {
  groups: SettingsNavGroup[]
  todo?: SettingsTodo
}) {
  const { translate } = useTranslation()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const totalTodo = Object.values(todo).reduce((sum, n) => sum + n, 0)

  return (
    <nav
      aria-label={translate("SETTINGS/TITLE")}
      className="flex flex-col gap-6"
    >
      <SettingsBackButton />
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-1">
          {group.labelKey && (
            <Typography as="muted" className="px-3">
              {translate(group.labelKey)}
            </Typography>
          )}
          {group.sections.map(({ id, icon: Icon, titleKey }) => (
            <NavLink
              key={id}
              to={SETTINGS_PATHS[id]}
              replace
              className={({ isActive }) =>
                cn(
                  buttonVariants({ variant: "ghost" }),
                  // Regular weight like the app sidebar; the current page
                  // medium, in the brand yellow, on hover too.
                  "justify-start font-normal",
                  isActive &&
                    "bg-primary/10 font-medium text-primary hover:bg-primary/15 hover:text-primary",
                )
              }
            >
              <Icon />
              {translate(titleKey)}
              <TodoMarker
                count={id === "setup" ? totalTodo : (todo[id] ?? 0)}
                total={id === "setup"}
              />
            </NavLink>
          ))}
        </div>
      ))}
      <Separator />
      <Button
        variant="ghost"
        className="justify-start font-normal"
        onClick={() => setFeedbackOpen(true)}
      >
        <MessageSquare />
        {translate("SETTINGS/FEEDBACK")}
      </Button>
      <FeedbackSubmissionModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
      />
    </nav>
  )
}

/** Small screens have no room for the nav column: a page picker instead. */
export function SettingsMobileNav({
  groups,
  currentId,
}: {
  groups: SettingsNavGroup[]
  currentId?: SettingsSectionId
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const confirmLeave = useConfirmLeave()

  return (
    <div className="flex items-center gap-2">
      <SettingsBackButton iconOnly />
      <Select
        // Controlled from the first render, also on a page that isn't listed.
        value={currentId ?? ""}
        onValueChange={(id) =>
          confirmLeave(
            () =>
              void navigate(SETTINGS_PATHS[id as SettingsSectionId], {
                replace: true,
              }),
          )
        }
      >
        <SelectTrigger aria-label={translate("SETTINGS/TITLE")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {groups.map((group) => (
            <SelectGroup key={group.id}>
              {group.labelKey && (
                <SelectLabel>{translate(group.labelKey)}</SelectLabel>
              )}
              {group.sections.map(({ id, titleKey }) => (
                <SelectItem key={id} value={id}>
                  {translate(titleKey)}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
